import React, { useState, useEffect } from 'react';
import { FaSearch, FaTimes, FaGift, FaTicketAlt, FaBox, FaPercent, FaDollarSign } from 'react-icons/fa';
import { MdCardGiftcard } from 'react-icons/md';
import { Button } from '@scalingworks/react-admin-ui';
import { useGetRewardsQuery } from '~/generated/hooks';
import { isEmpty } from 'lodash';
import { RewardType } from '~/api';

export interface RewardOption {
  id: string;
  name: string;
  type: 'VOUCHER' | 'PRODUCT' | 'DISCOUNT' | 'POINTS' | 'CASH_BACK' | 'REWARD';
  pointsCost?: number;
  description?: string;
  value?: string | number;
  imageUrl?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (reward: RewardOption) => void;
  selectedRewardId?: string;
  loading?: boolean;
}


export const RewardSelector: React.FC<Props> = (props) => {
  const {
    isOpen,
    onClose,
    onSelect,
    selectedRewardId,
    loading = false
  } = props;
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredRewards, setFilteredRewards] = useState<RewardOption[]>([]);

  useGetRewardsQuery({
    input: {
      search: searchTerm,
    },
  }, {
    onSuccess: (data) => {
      setFilteredRewards(data?.getRewards.items.map(reward => ({
        id: reward.id,
        name: reward.name,
        type: reward.type as unknown as RewardOption['type'],
        pointsCost: reward.pointCost,
        description: reward.description,
        imageUrl: reward.bigImage,
        value: reward.pointCost,
      })) as RewardOption[] || []);
    }
  });


  if (!isOpen) return null;

  const getRewardIcon = (type: RewardOption['type']) => {
    switch (type) {
      case 'VOUCHER':
        return <FaTicketAlt className="text-indigo-600" />;
      case 'PRODUCT':
        return <FaBox className="text-green-600" />;
      case 'DISCOUNT':
        return <FaPercent className="text-orange-600" />;
      case 'POINTS':
        return <MdCardGiftcard className="text-purple-600" />;
      case 'CASH_BACK':
        return <FaDollarSign className="text-emerald-600" />;
      case 'REWARD':
      default:
        return <FaGift className="text-indigo-600" />;
    }
  };

  const getRewardTypeLabel = (type: RewardOption['type']) => {
    const labels: Record<RewardOption['type'], string> = {
      'VOUCHER': 'Voucher',
      'PRODUCT': 'Product',
      'DISCOUNT': 'Discount',
      'POINTS': 'Points',
      'CASH_BACK': 'Cash Back',
      'REWARD': 'Reward'
    };
    return labels[type] || type;
  };

  const formatRewardValue = (reward: RewardOption): string | null => {
    if (!reward.value) return null;
    
    switch (reward.type) {
      case 'VOUCHER':
        return `RM${reward.value}`;
      case 'DISCOUNT':
        return `${reward.value}% off`;
      case 'CASH_BACK':
        return `RM${reward.value}`;
      case 'POINTS':
        return `${reward.value} points`;
      case 'PRODUCT':
        return null; // Products typically don't show value
      case 'REWARD':
      default:
        return String(reward.value);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Select Reward</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <FaTimes className="text-gray-500" />
            </button>
          </div>
          
          {/* Search bar */}
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search reward name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              autoFocus
            />
          </div>
        </div>

        {/* Rewards list */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-500">Loading rewards...</div>
            </div>
          ) : filteredRewards.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <FaGift className="text-4xl text-gray-300 mb-4" />
              <p className="text-gray-500">No rewards found</p>
              {searchTerm && (
                <p className="text-sm text-gray-400 mt-2">
                  Try adjusting your search term
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredRewards.map((reward) => (
                <button
                  key={reward.id}
                  onClick={() => onSelect(reward)}
                  className={`w-full p-4 rounded-lg border-2 transition-all hover:shadow-md ${
                    selectedRewardId === reward.id
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0">
                      {getRewardIcon(reward.type)}
                    </div>
                    <div className="flex-1 text-left">
                      <div className="font-medium text-gray-800">
                        {reward.name}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span className="bg-gray-100 px-2 py-0.5 rounded">
                          {getRewardTypeLabel(reward.type)}
                        </span>
                        {reward.pointsCost !== undefined && (
                          <span>({reward.pointsCost} pts)</span>
                        )}
                        {formatRewardValue(reward) && (
                          <span className="text-purple-600 font-medium">
                            {formatRewardValue(reward)}
                          </span>
                        )}
                      </div>
                      {reward.description && (
                        <p className="text-sm text-gray-600 mt-1">
                          {reward.description}
                        </p>
                      )}
                    </div>
                    {selectedRewardId === reward.id && (
                      <div className="flex-shrink-0">
                        <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200">
          <div className="flex justify-end gap-3">
            <Button
              variant="default"
              onClick={onClose}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};